import {
  Box,
  Chip,
  Link,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  useTheme,
} from "@mui/material";
import moment from "moment";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import InfoIcon from "@mui/icons-material/Info";
import LockIcon from "@mui/icons-material/Lock";
import {
  Button,
  ButtonGroup,
  Menu,
  Table,
  Typography,
  Input,
  Modal,
} from "../../../components";
import currency from "currency.js";
import {
  formatAvailability,
  formatMoney,
  formatNumber,
  formatNumberCompact,
  getBidStatusColor,
  getBidStatusLabel,
  shouldKeepCanceledCpfAsUsed,
} from "../../../utils";
import TagManager from "react-gtm-module";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { COMPANY_OPTIONS, COMPANY_OPTIONS_SORTED, PRIORITY_OPTIONS } from "../../../constants";
import type {
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import { useNavigate } from "react-router-dom";
import { useAxios, useToggle } from "../../../hooks";
import { APP_NAME } from "@next-pontos/settings";
import { User } from "../../../context/auth";
import { AirplaneTicket } from "@mui/icons-material";
import { groupBy, sumBy } from "lodash";
import { enqueueSnackbar } from "notistack";

type Program = {
  company: string;
  active: boolean;
  show_in_bids_filter: boolean;
};

const BID_STATUS_OPTIONS = [
  { value: "PENDING", label: "Pendente" },
  { value: "ACCEPTED", label: "Aceita" },
  { value: "DENIED", label: "Recusada" },
  { value: "AWAITING_ORDER", label: "Aguardando Emissão" },
  { value: "ON_ORDER", label: "Em Emissão" },
  { value: "DONE", label: "Finalizada" },
];

interface BidsTableProps {
  me: User | null;
  refetchBids: () => void;
  bids: any;
  setDenyBidId: (id: string) => void;
  denyModalVisibilityControls: { setTrue: () => void };
  setDeleteBidId: (id: string) => void;
  deleteModalVisibilityControls: { setTrue: () => void };
  fetchBid: (config: any) => void;
  accountModalVisibilityControls: { setTrue: () => void };
  paginationModel: GridPaginationModel;
  sortModel: GridSortModel;
  filterModel: GridFilterModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  onSortModelChange: (model: GridSortModel) => void;
  onFilterModelChange: (model: GridFilterModel) => void;
  rowCount: number;
  selectedCompanyValues: string[];
  onSelectedCompaniesChange: (values: string[]) => void;
}

export const BidsTable = ({
  me,
  bids,
  refetchBids,
  denyModalVisibilityControls,
  setDenyBidId,
  deleteModalVisibilityControls,
  setDeleteBidId,
  accountModalVisibilityControls,
  fetchBid,
  paginationModel,
  sortModel,
  filterModel,
  onPaginationModelChange,
  onSortModelChange,
  onFilterModelChange,
  rowCount,
  selectedCompanyValues,
  onSelectedCompaniesChange,
}: BidsTableProps) => {
  const confirmModalAction = useRef<null | (() => Promise<void>)>(null);
  const [rowSelectionModel, setRowSelectionModel] = useState<Array<string | number>>([]);
  // Cache of bids the user has selected, populated as they appear on the current
  // page. Needed for server-side pagination so selections from previous pages
  // (kept around via `keepNonExistentRowsSelected`) can still be inspected
  // client-side for the merge/unify action.
  const selectedBidsCache = useRef<Map<string | number, any>>(new Map());

  const [api, callAPI] = useAxios({}, { manual: true });
  const [programs] = useAxios("/program");

  const [confirmModalVisible, setConfirmModalVisibility] = useToggle(false);

  const theme = useTheme();
  const navigate = useNavigate();

  const [, postAPI] = useAxios(
    {
      method: "POST",
    },
    {
      manual: true,
    }
  );

  const handlePriorityChange = async (bidId: number, newPriority: string) => {
    await postAPI({
      method: "PUT",
      url: `/bid/${bidId}/priority`,
      data: { priority: newPriority },
    });
    refetchBids();
  };

  const handleToggleReserve = async (bidId: number) => {
    await postAPI({
      method: "PUT",
      url: `/bid/${bidId}/toggle-reserve`,
    });
    refetchBids();
  };

  const [reserveOfferVisibility, reserveOfferControls] = useToggle(false);
  const [reserveOfferBidId, setReserveOfferBidId] = useState<number | null>(null);
  const [reserveOfferReason, setReserveOfferReason] = useState("");

  const handleReserveOffer = async () => {
    if (!reserveOfferBidId) return;

    const reason = reserveOfferReason.trim();
    if (!reason) {
      enqueueSnackbar("Justificativa é obrigatória.", { variant: "error" });
      return;
    }

    await postAPI({
      method: "PUT",
      url: `/bid/${reserveOfferBidId}/toggle-reserve`,
      data: { reason },
    });

    reserveOfferControls.setFalse();
    setReserveOfferBidId(null);
    setReserveOfferReason("");
    refetchBids();
  };

  const [bidToFinish, setBidToFinish] = useState(null);
  const [finishOfferVisibility, finishOfferControls] = useToggle(false);

  const bidsRows: any[] = bids?.data?.rows || [];

  // Keep the cache in sync: ingest visible rows whose IDs are still selected,
  // drop entries that the user has deselected.
  useEffect(() => {
    const next = selectedBidsCache.current;
    for (const row of bidsRows) {
      if (rowSelectionModel.includes(row.id)) {
        next.set(row.id, row);
      }
    }
    for (const key of Array.from(next.keys())) {
      if (!rowSelectionModel.includes(key)) {
        next.delete(key);
      }
    }
  }, [bidsRows, rowSelectionModel]);

  const selectedBids: any[] = useMemo(() => {
    const fromCache = rowSelectionModel
      .map((id) => selectedBidsCache.current.get(id))
      .filter(Boolean);
    if (fromCache.length === rowSelectionModel.length) return fromCache;
    return bidsRows.filter((item: { id: number | string }) =>
      rowSelectionModel.includes(item.id),
    );
  }, [bidsRows, rowSelectionModel]);

  const companiesSortedAlphabetically = useMemo(() => {
    const programsData = (programs?.data as Program[] | undefined) ?? [];
    const allowedCompanies = new Set(
      programsData
        .filter((p) => p.show_in_bids_filter)
        .map((p) => p.company)
    );

    return [...COMPANY_OPTIONS]
      .filter((opt) => allowedCompanies.has(opt.value))
      .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
  }, [programs?.data]);

  // Drop selected companies that are no longer allowed (e.g. filter visibility disabled).
  useEffect(() => {
    if (!companiesSortedAlphabetically.length) return;
    const allowed = new Set(companiesSortedAlphabetically.map((c) => c.value));
    const filtered = selectedCompanyValues.filter((c) => allowed.has(c));
    if (filtered.length !== selectedCompanyValues.length) {
      onSelectedCompaniesChange(filtered);
    }
  }, [companiesSortedAlphabetically, selectedCompanyValues, onSelectedCompaniesChange]);

  const tableRows = useMemo(
    () =>
      bidsRows.map((item: any) => {
        // We prefer the denormalized `used_miles`/`used_pax`/`balance_miles`/
        // `balance_pax` returned by the API (kept up to date on every order
        // mutation), but recompute from `orders` as a fallback for older
        // payloads that may not yet contain those columns.
        const apiUsedMiles =
          item.used_miles !== undefined && item.used_miles !== null
            ? Number(item.used_miles)
            : null;
        const apiUsedPax =
          item.used_pax !== undefined && item.used_pax !== null
            ? Number(item.used_pax)
            : null;

        const fallbackUsedMiles =
          item?.orders
            ?.filter((order: { confirmation: string }) =>
              ["PENDING", "CONFIRMED"].includes(order.confirmation)
            )
            ?.reduce(
              (prev: number, curr: { miles: number }) =>
                currency(prev).add(curr.miles).value,
              0
            ) ?? 0;

        const fallbackUsedPax =
          item?.orders
            ?.filter((order: { confirmation: string }) =>
              shouldKeepCanceledCpfAsUsed(item.company)
                ? ["PENDING", "CONFIRMED", "CANCELLED"].includes(order.confirmation)
                : ["PENDING", "CONFIRMED"].includes(order.confirmation)
            )
            ?.reduce(
              (prev: number, curr: { pax: number }) =>
                currency(prev).add(curr.pax).value,
              0
            ) ?? 0;

        const used_miles = apiUsedMiles ?? fallbackUsedMiles;
        const used_pax = apiUsedPax ?? fallbackUsedPax;
        const pax = item.pax ? Number(item.pax) : null;
        const balance_miles =
          item.balance_miles !== undefined && item.balance_miles !== null
            ? Number(item.balance_miles)
            : currency(item.amount).subtract(used_miles).value;
        const balance_pax =
          pax === null
            ? null
            : item.balance_pax !== undefined && item.balance_pax !== null
            ? Number(item.balance_pax)
            : currency(pax).subtract(used_pax).value;

        return {
          ...item,
          id: item.id,
          user: me?.role === "AGENCY" ? "" : item.user?.name ?? "",
          user_id: me?.role === "AGENCY" ? "" : item.user?.id ?? "",
          inviter_id: me?.role === "AGENCY" ? "" : item.user?.inviter?.id ?? "",
          inviter_name: me?.role === "AGENCY" ? "" : item.user?.inviter?.name ?? "",
          created_at: item.created_at,
          company_value: item.company,
          used_miles,
          used_pax,
          balance_miles,
          balance_pax,
          price_per_k: item.price_per_k,
          pax,
          status: item.status,
          priority: item.priority,
          whatsapp: me?.role === "AGENCY" ? "" : item.user?.whatsapp ?? "",
          telegram: me?.role === "AGENCY" ? "" : item.user?.telegram ?? "",
          address_city: me?.role === "AGENCY" ? "" : item.user?.address_city ?? "",
          email: me?.role === "AGENCY" ? "" : item.user?.email ?? "",
          address_state: me?.role === "AGENCY" ? "" : item.user?.address_state ?? "",
          is_reserved: item.is_reserved,
          reservation_reason: item?.bid_reservation_reasons?.[0]?.reason ?? null,
        };
      }),
    [bidsRows, me?.role]
  );

  const mergedBidsAmount = useMemo(() => {
    const data: any[] =
      selectedBids?.map((item: any) => ({
        miles: item.amount,
        used_miles:
          item?.orders
            ?.filter((o: { confirmation: string }) =>
              ["PENDING", "CONFIRMED"].includes(o.confirmation)
            )
            ?.reduce(
              (prev: number, curr: { miles: number }) =>
                currency(prev).add(curr.miles).value,
              0
            ) ?? 0,
        used_pax:
          item?.orders
            ?.filter((order: { confirmation: string }) =>
              shouldKeepCanceledCpfAsUsed(item.company)
                ? ["PENDING", "CONFIRMED", "CANCELLED"].includes(order.confirmation)
                : ["PENDING", "CONFIRMED"].includes(order.confirmation)
            )
            ?.reduce(
              (prev: number, curr: { pax: number }) =>
                currency(prev).add(curr.pax).value,
              0
            ) ?? 0,
        price_per_k: item.price_per_k,
        pax: item.pax ? Number(item.pax) : null,
      })) || [];

    const totalMiles = sumBy(data, (item: any) =>
      currency(item.miles).subtract(item.used_miles).value
    );
    const totalCost = sumBy(data, (item: any) => {
      const availableMiles = currency(item.miles, { precision: 3 }).subtract(
        item.used_miles
      ).value;
      const k = currency(availableMiles, { precision: 3 }).divide(1000).value;

      return currency(k, { precision: 3 }).multiply(item.price_per_k).value;
    });
    const totalPax = data.find((item: { pax: null | number }) => item.pax === null)
      ? null
      : sumBy(data, (item: any) =>
          currency(item.pax).subtract(item.used_pax).value
        );

    return {
      totalMiles,
      totalPax,
      totalCost,
      totalCostPerK: currency(totalCost, { precision: 3 }).divide(
        currency(totalMiles, { precision: 3 }).divide(1000)
      ).value,
    };
  }, [selectedBids]);

  return (
    <>
      <Modal
        open={reserveOfferVisibility}
        title="Reservar oferta"
        onConfirm={handleReserveOffer}
        confirmLabel="Reservar"
        onCancel={() => {
          reserveOfferControls.setFalse();
          setReserveOfferBidId(null);
          setReserveOfferReason("");
        }}
        cancelLabel="Cancelar"
      >
        <div>
          <Typography mb={2}>
            Informe o motivo da reserva. Esta justificativa ficará visível no cadeado para outros emissores e admins.
          </Typography>
          <Input
            label="Justificativa"
            value={reserveOfferReason}
            onChange={(e) => setReserveOfferReason(e.target.value)}
            multiline
            fullWidth
          />
        </div>
      </Modal>

      <Modal
        open={finishOfferVisibility}
        title="Atenção!"
        onConfirm={async () => {
          await postAPI({ url: `/bid/${bidToFinish}/finish` });
          finishOfferControls.setFalse();
          refetchBids();
        }}
        confirmLabel="Confirmar"
        onCancel={finishOfferControls.setFalse}
        cancelLabel="Cancelar"
      >
        <div>
          Tem certeza que você deseja <strong>FINALIZAR</strong> esta oferta?
        </div>
      </Modal>

      {confirmModalAction.current && confirmModalVisible && (
        <Modal
          title="Atenção!"
          open={confirmModalVisible}
          onConfirm={confirmModalAction.current}
          confirmLabel="Confirmar"
          onClose={() => {
            setConfirmModalVisibility.setFalse();
            confirmModalAction.current = null;
          }}
        >
          <div>
            Esta ação é irreversível e não pode ser desfeita.<br />
            Você tem certeza que deseja unificar estas ofertas?<br />
            Todas as ofertas selecionadas serão FINALIZADAS e será criada uma nova oferta com os seguintes parâmetros:<br />
            <br />
            <p><strong>Milhas:</strong> {formatNumber(mergedBidsAmount.totalMiles)}</p>
            <p><strong>R$/k:</strong> {formatMoney(mergedBidsAmount.totalCostPerK)}</p>
            <p><strong>CPFs:</strong> {mergedBidsAmount.totalPax === null ? "∞" : mergedBidsAmount.totalPax}</p>
          </div>
        </Modal>
      )}

      {rowSelectionModel.length ? (
        <div style={{ marginBottom: 10 }}>
          <Button
            disabled={rowSelectionModel.length < 2 || api.loading}
            onClick={() => {
              const groupedByUserIds = groupBy(selectedBids, "user_id");
              const groupedByAccountIds = groupBy(selectedBids, "account_id");

              if (Object.keys(groupedByUserIds).length > 1) {
                return enqueueSnackbar("As ofertas selecionadas são de autores diferentes");
              }

              if (Object.keys(groupedByAccountIds).length > 1) {
                return enqueueSnackbar("As ofertas selecionadas são de contas diferentes");
              }

              confirmModalAction.current = async () => {
                await callAPI({
                  url: `/bid/merge`,
                  method: "POST",
                  data: {
                    ids: rowSelectionModel,
                  },
                });

                refetchBids();
                setConfirmModalVisibility.setFalse();

                enqueueSnackbar("As ofertas foram unificadas com sucesso!", {
                  variant: "success",
                });
              };
              setConfirmModalVisibility.setTrue();
            }}
          >
            Unificar
          </Button>
        </div>
      ) : null}

      {companiesSortedAlphabetically.length > 0 && (
        <Box sx={{ mb: 3, width: 1 }}>
          <Box
            sx={{
              overflowX: "auto",
              pb: 0.5,
              "& .MuiToggleButtonGroup-root": { flexWrap: "nowrap" },
            }}
          >
            <ToggleButtonGroup
              value={selectedCompanyValues}
              onChange={(_, next) => onSelectedCompaniesChange(next ?? [])}
              aria-label="Filtrar por companhia"
              size="small"
            >
              {companiesSortedAlphabetically.map((opt) => (
                <ToggleButton
                  key={opt.value}
                  value={opt.value}
                  sx={{ whiteSpace: "nowrap", textTransform: "none" }}
                >
                  {opt.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        </Box>
      )}

      <Table
        initialState={{
          columns: {
            columnVisibilityModel: {
              actions: me?.role !== "AGENCY",
              user: me?.role !== "AGENCY",
              accepts_custom_payment: false,
              contact_availability: false,
            },
          },
        }}
        loading={bids.loading || api.loading}
        paginationMode="server"
        sortingMode="server"
        filterMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        filterModel={filterModel}
        onFilterModelChange={onFilterModelChange}
        rowCount={rowCount}
        pageSizeOptions={[10, 25, 50, 100]}
        keepNonExistentRowsSelected
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 400 },
          },
        }}
        processRowUpdate={async (newRow) => {
          await handlePriorityChange(newRow.id, newRow.priority);
          return newRow;
        }}
        getRowClassName={(params) => {
          const olderThanWeek = moment().diff(params.row.created_at, "days") >= 7;
          const isReserved = params.row.is_reserved;

          return [
            olderThanWeek && params.row.status === "ACCEPTED" ? `row-color-danger` : "",
            isReserved ? `row-color-reserved` : "",
          ].join(" ");
        }}
        rowSelection={true}
        checkboxSelection
        disableRowSelectionOnClick
        onRowSelectionModelChange={(newRowSelectionModel) => {
          setRowSelectionModel(newRowSelectionModel);
        }}
        rowSelectionModel={rowSelectionModel}
        isRowSelectable={({ row }) => {
          if (["DENIED", "PENDING", "DONE"].includes(row.status)) {
            return false;
          }

          if (rowSelectionModel.length === 0) {
            return true;
          }

          // Reach into the selection cache so the constraint keeps working
          // across pages even when the first selected bid isn't on the
          // currently rendered page.
          const firstSelectionId = rowSelectionModel[0];
          const firstSelection =
            selectedBidsCache.current.get(firstSelectionId) ??
            bidsRows.find((item) => item.id === firstSelectionId);

          if (!firstSelection) {
            // Until we ingest the first selection, fall back to allowing
            // selection; the merge action still validates client + server.
            return true;
          }

          return (
            firstSelection.user_id === row.user_id &&
            firstSelection.account_id === row.account_id
          );
        }}
        columns={[
          {
            field: "is_reserved",
            headerName: "",
            width: 50,
            type: "boolean",
            renderCell: ({ row }) => {
              if (row.is_reserved) {
                return (
                  <Tooltip title={row.reservation_reason || "Oferta Reservada"}>
                    <LockIcon color="action" />
                  </Tooltip>
                );
              }
              return null;
            },
          },
          { field: "id", headerName: "ID", minWidth: 60, type: "string" },
          {
            field: "created_at",
            headerName: "Data",
            flex: 1,
            maxWidth: 130,
            type: "date",
            valueGetter: ({ value }) => (value ? new Date(value) : null),
            valueFormatter: ({ value }) =>
              value ? moment(value).format("DD/MM/YYYY HH:mm:ss") : "",
          },
          {
            field: "user",
            headerName: "Fornecedor",
            flex: 1,
            maxWidth: 120,
            renderCell: ({ row }) => {
              return (
                <div style={{ display: "flex" }}>
                  {row.inviter_id && (
                    <Fragment>
                      <Tooltip title={`Indicado por ${row.inviter_name}`}>
                        <InfoIcon fontSize="small" />
                      </Tooltip>
                      &nbsp;&nbsp;&nbsp;
                    </Fragment>
                  )}
                  <Link href={`/admin/usuarios/${row.user_id}`}>{row.user}</Link>
                </div>
              );
            },
          },
          {
            field: "account.holder_name",
            headerName: "Titular",
            flex: 1,
            maxWidth: 120,
            valueGetter: ({ row }) => {
              return row?.account?.holder_name ?? "";
            },
          },
          {
            field: "company",
            headerName: "Programa",
            flex: 1,
            maxWidth: 130,
            type: "singleSelect",
            valueOptions: COMPANY_OPTIONS_SORTED.map((c) => ({
              value: c.value,
              label: c.label,
            })),
            valueFormatter: ({ value }) =>
              COMPANY_OPTIONS.find((cia) => cia.value === value)?.label ?? value,
          },
          {
            field: "balance_miles",
            headerName: "Quantidade",
            description:
              "Saldo restante de milhas (Quantidade total - Já Utilizado). Ordenação e filtros operam sobre este saldo.",
            flex: 1,
            maxWidth: 130,
            type: "number",
            renderCell: ({ value, row }) => {
              const usedMiles = Number(row.used_miles ?? 0);
              return (
                <Fragment>
                  <Typography variant="body2">
                    {Number(value ?? 0).toLocaleString("pt-BR")}
                  </Typography>
                  {usedMiles > 0 && (
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      marginLeft="5px"
                      color={theme.palette.error.main}
                    >
                      (-{formatNumberCompact(usedMiles)})
                    </Typography>
                  )}
                </Fragment>
              );
            },
            valueGetter: ({ row }) => {
              // Prefer the denormalized field; fall back to a client-side
              // computation in case an older API response sneaks in.
              if (row.balance_miles !== undefined && row.balance_miles !== null) {
                return Number(row.balance_miles);
              }
              return currency(row.amount).subtract(row.used_miles ?? 0).value;
            },
          },
          {
            field: "price_per_k",
            headerName: "R$/k",
            maxWidth: 80,
            type: "number",
            valueFormatter: ({ value }) => {
              return currency(value).format({
                symbol: "R$ ",
                decimal: ",",
                separator: ".",
              });
            },
          },
          {
            field: "balance_pax",
            headerName: "CPF",
            description:
              "Saldo restante de CPFs (CPF total - Já Utilizado). Ofertas sem limite de CPFs aparecem como ∞.",
            flex: 1,
            maxWidth: 60,
            type: "number",
            renderCell: ({ row }) => {
              const usedPax = Number(row.used_pax ?? 0);
              const hasPaxLimit = row.pax !== null && row.pax !== undefined;
              const balancePax = hasPaxLimit
                ? row.balance_pax !== undefined && row.balance_pax !== null
                  ? Number(row.balance_pax)
                  : currency(row.pax).subtract(usedPax).value
                : null;
              return (
                <Fragment>
                  <Typography variant="body2">
                    {hasPaxLimit ? balancePax : "∞"}
                  </Typography>
                  {hasPaxLimit && usedPax > 0 && (
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      marginLeft="5px"
                      color={theme.palette.error.main}
                    >
                      (-{usedPax})
                    </Typography>
                  )}
                </Fragment>
              );
            },
            valueGetter: ({ row }) => {
              if (row.pax === null || row.pax === undefined) return null;
              if (row.balance_pax !== undefined && row.balance_pax !== null) {
                return Number(row.balance_pax);
              }
              return currency(row.pax).subtract(row.used_pax ?? 0).value;
            },
          },
          {
            field: "average_price",
            headerName: "Média por CPF",
            type: "number",
            flex: 1,
            maxWidth: 100,
            sortable: true,
            filterable: false,
            valueFormatter(params) {
              return params.value === Infinity
                ? "∞"
                : formatNumberCompact(params.value);
            },
            valueGetter: ({ row }) => {
              const availableMiles = currency(row.amount).subtract(row.used_miles).value;
              const availablePax = row.pax - row.used_pax;

              return row.pax === null
                ? Infinity
                : currency(availableMiles).divide(availablePax).value;
            },
          },
          {
            field: "contact_availability",
            headerName: "Disp. Contato",
            type: "text",
            flex: 1,
            maxWidth: 140,
            sortable: false,
            filterable: false,
            valueGetter: (item) => {
              return typeof item.row.bid_contact_availability === "object"
                ? formatAvailability(item.row.bid_contact_availability)
                : "";
            },
          },
          {
            field: "accepts_custom_payment",
            headerName: "Aceita Pag. à Prazo",
            type: "boolean",
            flex: 1,
            maxWidth: 140,
          },
          {
            field: "priority",
            headerName: "Prioridade",
            flex: 1,
            maxWidth: 140,
            type: "singleSelect",
            valueOptions: PRIORITY_OPTIONS.map((opt) => ({
              value: opt.value,
              label: opt.label,
            })),
            renderCell: ({ row }) => {
              return (
                <Input
                  variant="standard"
                  select
                  value={row.priority}
                  options={PRIORITY_OPTIONS}
                  onChange={async (e) => {
                    await handlePriorityChange(row.id, e.target.value);
                  }}
                  disabled={me?.role !== "ADMIN"}
                  sx={{
                    "& .MuiInput-underline:before": { borderBottom: "none" },
                    "& .MuiInput-underline:disabled": { borderBottom: "none" },
                    "& .MuiInput-underline:after": { borderBottom: "none" },
                    "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                      borderBottom: "none",
                    },
                  }}
                />
              );
            },
          },
          {
            field: "status",
            headerName: "Status",
            flex: 1,
            maxWidth: 140,
            type: "singleSelect",
            valueOptions: BID_STATUS_OPTIONS,
            renderCell: ({ value, row }) => {
              return (
                <Chip
                  color={getBidStatusColor(row.status)}
                  label={getBidStatusLabel(value)}
                />
              );
            },
          },
          {
            field: "actions",
            headerName: "",
            hideable: false,
            width: 150,
            align: "right",
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: ({ row }) => {
              let showEmitir = false;

              const messageText = encodeURI(
                `Olá, ${row.user}! Tudo bem? Me chamo ${me?.name} e estou entrando em contato para falar da sua oferta número ${row.id} na ${APP_NAME}.`
              );

              let menuOptions = [
                {
                  label: "Ver Detalhes",
                  onClick: async () => {
                    navigate(`/admin/ofertas/${row.id}`);
                  },
                },
              ];

              if (me?.role === "ADMIN") {
                menuOptions.push({
                  label: row.is_reserved ? "Liberar Oferta" : "Reservar Oferta",
                  onClick: async () => {
                    if (row.is_reserved) {
                      await handleToggleReserve(row.id);
                      return;
                    }

                    setReserveOfferBidId(row.id);
                    setReserveOfferReason("");
                    reserveOfferControls.setTrue();
                  },
                });
              }

              if (
                (me?.role === "ADMIN" || me?.role === "SELLER") &&
                (row.status === "PENDING" || row.status === "DENIED") &&
                !row.is_reserved
              ) {
                menuOptions.push({
                  label: "Aceitar",
                  onClick: async () => {
                    TagManager.dataLayer({
                      dataLayer: {
                        event: "purchase",
                        value: currency(
                          currency(row.amount).divide(1000).value
                        ).multiply(row.price_per_k).value,
                        transaction_id: row.id,
                        currency: "BRL",
                        items: [
                          {
                            item_id: row.id,
                            item_name: row.company,
                            price: row.price_per_k,
                            quantity: currency(row.amount).divide(1000).value,
                          },
                        ],
                      },
                    });

                    await postAPI({ url: `/bid/${row.id}/start-order` });
                    refetchBids();
                  },
                });
              }

              if (
                me?.role === "ADMIN" &&
                (row.status === "PENDING" ||
                  row.status === "ACCEPTED" ||
                  row.status === "AWAITING_ORDER" ||
                  row.status === "ON_ORDER")
              ) {
                menuOptions.push({
                  label: "Recusar",
                  onClick: async () => {
                    setDenyBidId(row.id);
                    denyModalVisibilityControls.setTrue();
                  },
                });
              }

              if (row.status === "ACCEPTED") {
                menuOptions.push({
                  label: "Habilitar para Emissões",
                  onClick: async () => {
                    await postAPI(`/bid/${row.id}/start-order?send_email=false`);
                    refetchBids();
                  },
                });
              }

              if (row.status === "ACCEPTED" || row.status === "DENIED") {
                menuOptions.push({
                  label: "Excluir",
                  onClick: async () => {
                    setDeleteBidId(row.id);
                    deleteModalVisibilityControls.setTrue();
                  },
                });
              }

              menuOptions.push({
                label: "Ver Conta",
                onClick: async () => {
                  fetchBid({
                    url: `/bid/${row.id}`,
                  });

                  accountModalVisibilityControls.setTrue();
                },
              });

              if (row.status === "AWAITING_ORDER" || row.status === "ON_ORDER") {
                if (!row.is_reserved) {
                  showEmitir = true;
                  menuOptions.push({
                    label: "Emitir",
                    onClick: async () => {
                      navigate(`/admin/ofertas/${row.id}/emissao`);
                    },
                  });
                }
              }

              if (row.status === "ON_ORDER") {
                menuOptions.push({
                  label: "Voltar para Preparação",
                  onClick: async () => {
                    await postAPI(`/bid/${row.id}/prepare`);
                    refetchBids();
                  },
                });

                if (me?.role === "ADMIN") {
                  menuOptions.push({
                    label: "Finalizar",
                    onClick: async () => {
                      setBidToFinish(row.id);
                      finishOfferControls.setTrue();
                    },
                  });
                }
              }

              if (me?.role === "SELLER") {
                menuOptions = menuOptions.filter((item) => {
                  return (
                    item.label === "Emitir" ||
                    item.label === "Ver Conta" ||
                    "Voltar para Preparação"
                  );
                });
              }

              return (
                <ButtonGroup size="small">
                  {showEmitir && (
                    <Button
                      variant="outlined"
                      onClick={() =>
                        navigate(`/admin/ofertas/${row.id}/emissao`)
                      }
                    >
                      <AirplaneTicket />
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    onClick={() =>
                      window.open(
                        `https://api.whatsapp.com/send?phone=${row.whatsapp}&text=${messageText}`,
                        "_blank"
                      )
                    }
                  >
                    <WhatsAppIcon />
                  </Button>
                  {["ADMIN", "SELLER"].includes(me?.role as string) && (
                    <Menu options={menuOptions}>
                      <MoreHorizIcon />
                    </Menu>
                  )}
                </ButtonGroup>
              );
            },
          },
        ]}
        rows={tableRows}
      />
    </>
  );
};
